import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Ticket, 
  Calendar, 
  Settings,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Users,
  Star,
  Clock,
  Download,
  Search,
  AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const AdminTickets = () => {
  const { user } = useContext(AuthContext);
  const [seasons, setSeasons] = useState([]);
  const [games, setGames] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('seasons');
  
  // Dialogs
  const [showSeasonDialog, setShowSeasonDialog] = useState(false);
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);
  const [editingGame, setEditingGame] = useState(null);
  
  // Forms
  const [seasonForm, setSeasonForm] = useState({
    name: '',
    free_ticket_quota_per_user: 10,
    free_vip_upgrade_quota_per_user: 5,
    default_max_free_tickets_per_game: 4,
    default_max_free_upgrades_per_game: 2,
    default_ticket_price: 25.00,
    default_vip_upgrade_price: 15.00
  });
  
  const [rulesForm, setRulesForm] = useState({
    order_deadline: '',
    max_free_tickets_per_user: '',
    max_free_upgrades_per_user: '',
    ticket_price: '',
    vip_upgrade_price: '',
    is_special_event: false,
    tickets_enabled: true
  });
  
  // Filters
  const [orderEventFilter, setOrderEventFilter] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [seasonsRes, gamesRes, ordersRes] = await Promise.all([
        axios.get('/tickets/seasons'),
        axios.get('/tickets/games'),
        axios.get('/tickets/admin/orders?include_cancelled=true')
      ]);
      setSeasons(seasonsRes.data);
      setGames(gamesRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return format(new Date(dateStr), 'dd.MM.yyyy HH:mm', { locale: de });
  };

  // Season Management
  const openSeasonDialog = (season = null) => {
    if (season) {
      setEditingSeason(season);
      setSeasonForm({
        name: season.name,
        free_ticket_quota_per_user: season.free_ticket_quota_per_user,
        free_vip_upgrade_quota_per_user: season.free_vip_upgrade_quota_per_user,
        default_max_free_tickets_per_game: season.default_max_free_tickets_per_game,
        default_max_free_upgrades_per_game: season.default_max_free_upgrades_per_game,
        default_ticket_price: season.default_ticket_price,
        default_vip_upgrade_price: season.default_vip_upgrade_price
      });
    } else {
      setEditingSeason(null);
      setSeasonForm({
        name: '',
        free_ticket_quota_per_user: 10,
        free_vip_upgrade_quota_per_user: 5,
        default_max_free_tickets_per_game: 4,
        default_max_free_upgrades_per_game: 2,
        default_ticket_price: 25.00,
        default_vip_upgrade_price: 15.00
      });
    }
    setShowSeasonDialog(true);
  };

  const saveSeason = async () => {
    if (!seasonForm.name) {
      toast.error('Bitte Namen eingeben');
      return;
    }
    
    try {
      if (editingSeason) {
        await axios.patch(`/tickets/seasons/${editingSeason.id}`, seasonForm);
        toast.success('Saison aktualisiert');
      } else {
        await axios.post('/tickets/seasons', seasonForm);
        toast.success('Saison erstellt');
      }
      setShowSeasonDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const activateSeason = async (seasonId) => {
    try {
      await axios.patch(`/tickets/seasons/${seasonId}`, { is_active: true });
      toast.success('Saison aktiviert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Aktivieren');
    }
  };

  const deleteSeason = async (seasonId) => {
    if (!confirm('Saison wirklich löschen? Dies ist nur möglich, wenn keine Bestellungen existieren.')) return;
    
    try {
      await axios.delete(`/tickets/seasons/${seasonId}`);
      toast.success('Saison gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Game Rules Management
  const openRulesDialog = (game) => {
    setEditingGame(game);
    const rules = game.ticket_rules || {};
    setRulesForm({
      order_deadline: rules.order_deadline ? new Date(rules.order_deadline).toISOString().slice(0, 16) : '',
      max_free_tickets_per_user: rules.max_free_tickets_per_user || '',
      max_free_upgrades_per_user: rules.max_free_upgrades_per_user || '',
      ticket_price: rules.ticket_price || '',
      vip_upgrade_price: rules.vip_upgrade_price || '',
      is_special_event: rules.is_special_event || false,
      tickets_enabled: rules.tickets_enabled !== false
    });
    setShowRulesDialog(true);
  };

  const saveRules = async () => {
    try {
      const payload = {
        event_id: editingGame.id,
        order_deadline: rulesForm.order_deadline ? new Date(rulesForm.order_deadline).toISOString() : null,
        max_free_tickets_per_user: rulesForm.max_free_tickets_per_user ? parseInt(rulesForm.max_free_tickets_per_user) : null,
        max_free_upgrades_per_user: rulesForm.max_free_upgrades_per_user ? parseInt(rulesForm.max_free_upgrades_per_user) : null,
        ticket_price: rulesForm.ticket_price ? parseFloat(rulesForm.ticket_price) : null,
        vip_upgrade_price: rulesForm.vip_upgrade_price ? parseFloat(rulesForm.vip_upgrade_price) : null,
        is_special_event: rulesForm.is_special_event,
        tickets_enabled: rulesForm.tickets_enabled
      };
      
      await axios.post('/tickets/games/rules', payload);
      toast.success('Regeln gespeichert');
      setShowRulesDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  // Export Orders
  const exportOrders = () => {
    const filteredOrders = orders.filter(o => {
      if (orderEventFilter && o.event_id !== orderEventFilter) return false;
      if (!showCancelled && o.is_cancelled) return false;
      return true;
    });
    
    const csv = [
      ['Event', 'Datum', 'Mitarbeiter', 'Kostenlose Tickets', 'Kostenlose Upgrades', 'Gekaufte Tickets', 'Gekaufte Upgrades', 'Lieferart', 'Status', 'Bestellt am'].join(';'),
      ...filteredOrders.map(o => [
        o.event_title,
        o.event_date ? format(new Date(o.event_date), 'dd.MM.yyyy') : '',
        o.user_name,
        o.free_tickets,
        o.free_upgrades,
        o.purchased_tickets,
        o.purchased_upgrades,
        o.delivery_type,
        o.is_cancelled ? 'Storniert' : 'Aktiv',
        format(new Date(o.created_at), 'dd.MM.yyyy HH:mm')
      ].join(';'))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ticket-bestellungen-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const activeSeason = seasons.find(s => s.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#e30613] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-tickets-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Ticket className="w-6 h-6 text-[#e30613]" />
          Ticket-Verwaltung
        </h1>
        {activeSeason && (
          <Badge className="bg-green-500">
            Aktive Saison: {activeSeason.name}
          </Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="seasons" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Saisons
          </TabsTrigger>
          <TabsTrigger value="games" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Spiel-Regeln
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Bestellungen
          </TabsTrigger>
        </TabsList>

        {/* Seasons Tab */}
        <TabsContent value="seasons" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => openSeasonDialog()} className="bg-[#e30613] hover:bg-[#c00510]">
              <Plus className="w-4 h-4 mr-2" />
              Neue Saison
            </Button>
          </div>

          <div className="grid gap-4">
            {seasons.length === 0 ? (
              <Card className="text-center py-8">
                <CardContent>
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Keine Saisons vorhanden</p>
                </CardContent>
              </Card>
            ) : (
              seasons.map((season) => (
                <Card key={season.id} className={season.is_active ? 'border-green-500 border-2' : ''}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CardTitle>{season.name}</CardTitle>
                        {season.is_active && (
                          <Badge className="bg-green-500">Aktiv</Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {!season.is_active && (
                          <Button size="sm" variant="outline" onClick={() => activateSeason(season.id)}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aktivieren
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openSeasonDialog(season)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deleteSeason(season.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Tickets/User</p>
                        <p className="font-semibold">{season.free_ticket_quota_per_user}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Upgrades/User</p>
                        <p className="font-semibold">{season.free_vip_upgrade_quota_per_user}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Max. Tickets/Spiel</p>
                        <p className="font-semibold">{season.default_max_free_tickets_per_game}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Max. Upgrades/Spiel</p>
                        <p className="font-semibold">{season.default_max_free_upgrades_per_game}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                      <div>
                        <p className="text-gray-500">Ticket-Preis</p>
                        <p className="font-semibold">{season.default_ticket_price?.toFixed(2)} €</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Upgrade-Preis</p>
                        <p className="font-semibold">{season.default_vip_upgrade_price?.toFixed(2)} €</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Games Tab */}
        <TabsContent value="games" className="space-y-4">
          {!activeSeason ? (
            <Card className="text-center py-8 bg-yellow-50 dark:bg-yellow-900/20">
              <CardContent>
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-600">Bitte erst eine Saison aktivieren</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {games.length === 0 ? (
                <Card className="text-center py-8">
                  <CardContent>
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Keine Heimspiele vorhanden</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Erstellen Sie im Intranet Events mit aktivierter "Heimspieltag"-Option
                    </p>
                  </CardContent>
                </Card>
              ) : (
                games.map((game) => (
                  <Card key={game.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold">{game.title}</h3>
                            {game.is_special_event && (
                              <Badge variant="outline" className="border-purple-500 text-purple-500">
                                <Star className="w-3 h-3 mr-1" />
                                Sonder-Event
                              </Badge>
                            )}
                            {!game.tickets_enabled && (
                              <Badge variant="destructive">Deaktiviert</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {formatDate(game.start_date)}
                          </p>
                          <div className="flex gap-4 mt-2 text-sm">
                            <span>
                              Max. Tickets: <strong>{game.max_free_tickets}</strong>
                              {game.ticket_rules?.max_free_tickets_per_user && ' (Override)'}
                            </span>
                            <span>
                              Max. Upgrades: <strong>{game.max_free_upgrades}</strong>
                              {game.ticket_rules?.max_free_upgrades_per_user && ' (Override)'}
                            </span>
                            <span>
                              Ticket: <strong>{game.ticket_price?.toFixed(2)} €</strong>
                            </span>
                            <span>
                              Upgrade: <strong>{game.vip_upgrade_price?.toFixed(2)} €</strong>
                            </span>
                          </div>
                          {game.order_deadline && (
                            <p className="text-xs text-orange-500 mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              Deadline: {formatDate(game.order_deadline)}
                            </p>
                          )}
                        </div>
                        <Button variant="outline" onClick={() => openRulesDialog(game)}>
                          <Settings className="w-4 h-4 mr-2" />
                          Regeln
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <select
                value={orderEventFilter}
                onChange={(e) => setOrderEventFilter(e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="">Alle Events</option>
                {[...new Set(orders.map(o => o.event_id))].map(eventId => {
                  const order = orders.find(o => o.event_id === eventId);
                  return (
                    <option key={eventId} value={eventId}>
                      {order?.event_title}
                    </option>
                  );
                })}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
              />
              Stornierte anzeigen
            </label>
            <Button variant="outline" onClick={exportOrders}>
              <Download className="w-4 h-4 mr-2" />
              CSV Export
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="p-3 text-left">Event</th>
                  <th className="p-3 text-left">Mitarbeiter</th>
                  <th className="p-3 text-center">Kostenlos</th>
                  <th className="p-3 text-center">Gekauft</th>
                  <th className="p-3 text-left">Lieferart</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Bestellt</th>
                </tr>
              </thead>
              <tbody>
                {orders
                  .filter(o => {
                    if (orderEventFilter && o.event_id !== orderEventFilter) return false;
                    if (!showCancelled && o.is_cancelled) return false;
                    return true;
                  })
                  .map((order) => (
                    <tr key={order.id} className={`border-t ${order.is_cancelled ? 'opacity-50' : ''}`}>
                      <td className="p-3">
                        <div>
                          <p className="font-medium">{order.event_title}</p>
                          <p className="text-xs text-gray-500">{order.event_date && formatDate(order.event_date)}</p>
                        </div>
                      </td>
                      <td className="p-3">{order.user_name}</td>
                      <td className="p-3 text-center">
                        {order.free_tickets}T / {order.free_upgrades}U
                      </td>
                      <td className="p-3 text-center">
                        {order.purchased_tickets}T / {order.purchased_upgrades}U
                      </td>
                      <td className="p-3">
                        {order.delivery_type === 'fach' ? 'Ins Fach' :
                         order.delivery_type === 'digital' ? 'Digital' :
                         `Hinterlegt: ${order.hinterlegt_vorname} ${order.hinterlegt_nachname}`}
                      </td>
                      <td className="p-3">
                        {order.is_cancelled ? (
                          <Badge variant="destructive">Storniert</Badge>
                        ) : (
                          <Badge className="bg-green-500">Aktiv</Badge>
                        )}
                      </td>
                      <td className="p-3 text-xs text-gray-500">
                        {formatDate(order.created_at)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Season Dialog */}
      <Dialog open={showSeasonDialog} onOpenChange={setShowSeasonDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSeason ? 'Saison bearbeiten' : 'Neue Saison erstellen'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={seasonForm.name}
                onChange={(e) => setSeasonForm({...seasonForm, name: e.target.value})}
                placeholder="z.B. Saison 2026/27"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tickets pro User (Saison)</Label>
                <Input
                  type="number"
                  min={0}
                  value={seasonForm.free_ticket_quota_per_user}
                  onChange={(e) => setSeasonForm({...seasonForm, free_ticket_quota_per_user: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label>Upgrades pro User (Saison)</Label>
                <Input
                  type="number"
                  min={0}
                  value={seasonForm.free_vip_upgrade_quota_per_user}
                  onChange={(e) => setSeasonForm({...seasonForm, free_vip_upgrade_quota_per_user: parseInt(e.target.value)})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max. Tickets pro Spiel</Label>
                <Input
                  type="number"
                  min={0}
                  value={seasonForm.default_max_free_tickets_per_game}
                  onChange={(e) => setSeasonForm({...seasonForm, default_max_free_tickets_per_game: parseInt(e.target.value)})}
                />
              </div>
              <div>
                <Label>Max. Upgrades pro Spiel</Label>
                <Input
                  type="number"
                  min={0}
                  value={seasonForm.default_max_free_upgrades_per_game}
                  onChange={(e) => setSeasonForm({...seasonForm, default_max_free_upgrades_per_game: parseInt(e.target.value)})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ticket-Preis (€)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={seasonForm.default_ticket_price}
                  onChange={(e) => setSeasonForm({...seasonForm, default_ticket_price: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label>Upgrade-Preis (€)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={seasonForm.default_vip_upgrade_price}
                  onChange={(e) => setSeasonForm({...seasonForm, default_vip_upgrade_price: parseFloat(e.target.value)})}
                />
              </div>
            </div>
            
            <div className="flex gap-3 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowSeasonDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={saveSeason} className="bg-[#e30613] hover:bg-[#c00510]">
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rules Dialog */}
      <Dialog open={showRulesDialog} onOpenChange={setShowRulesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Spiel-Regeln: {editingGame?.title}</DialogTitle>
            <DialogDescription>
              Überschreiben Sie hier die Saison-Standardwerte für dieses Spiel
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Bestellfrist (optional)</Label>
              <Input
                type="datetime-local"
                value={rulesForm.order_deadline}
                onChange={(e) => setRulesForm({...rulesForm, order_deadline: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max. Tickets (leer = Standard)</Label>
                <Input
                  type="number"
                  min={0}
                  value={rulesForm.max_free_tickets_per_user}
                  onChange={(e) => setRulesForm({...rulesForm, max_free_tickets_per_user: e.target.value})}
                  placeholder={activeSeason?.default_max_free_tickets_per_game?.toString()}
                />
              </div>
              <div>
                <Label>Max. Upgrades (leer = Standard)</Label>
                <Input
                  type="number"
                  min={0}
                  value={rulesForm.max_free_upgrades_per_user}
                  onChange={(e) => setRulesForm({...rulesForm, max_free_upgrades_per_user: e.target.value})}
                  placeholder={activeSeason?.default_max_free_upgrades_per_game?.toString()}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ticket-Preis € (leer = Standard)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={rulesForm.ticket_price}
                  onChange={(e) => setRulesForm({...rulesForm, ticket_price: e.target.value})}
                  placeholder={activeSeason?.default_ticket_price?.toFixed(2)}
                />
              </div>
              <div>
                <Label>Upgrade-Preis € (leer = Standard)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={rulesForm.vip_upgrade_price}
                  onChange={(e) => setRulesForm({...rulesForm, vip_upgrade_price: e.target.value})}
                  placeholder={activeSeason?.default_vip_upgrade_price?.toFixed(2)}
                />
              </div>
            </div>
            
            <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rulesForm.tickets_enabled}
                  onChange={(e) => setRulesForm({...rulesForm, tickets_enabled: e.target.checked})}
                />
                <span>Tickets aktiviert</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rulesForm.is_special_event}
                  onChange={(e) => setRulesForm({...rulesForm, is_special_event: e.target.checked})}
                />
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-purple-500" />
                  Sonder-Event (zählt nicht gegen Saison-Kontingent)
                </span>
              </label>
            </div>
            
            <div className="flex gap-3 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowRulesDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={saveRules} className="bg-[#e30613] hover:bg-[#c00510]">
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTickets;
